import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Icon } from '@/components/icons';
import { ActiveContext, Context } from '@/types';
import { NavLink } from './NavLink';
import { Modal } from '@/components/ui/Modal';
import { Avatar, AvatarGender } from '@/components/ui';
import { C1Actions, C1Button } from '@/components/c1';
import { ProfileCard } from './ProfileCard';
import { useState } from 'react';
import { useSession, useMediaQuery } from '@/hooks';
import { isPlatformAdmin, isInstitutionAdmin } from '@/auth/session';
import { EASE, DURATION } from '@/components/ui/motion';
import { useEduCoreLaunchUrl } from '@/hooks/useEduCoreLaunchUrl';
import { EduCorePaths } from '@/lib/educoreLinks';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  onSwitchContext: () => void;
  activeContext: ActiveContext | null;
  availableContexts: Context[];
  onLogout: () => void;
  profileName?: string;
  profileEmail?: string;
  /** Drives the DiceBear avatar style; falls back to a neutral style when absent. */
  profileGender?: AvatarGender | null;
  portalTitle?: string;
  /** A blob URL for the institution's own logo; the platform logo is the fallback. */
  logoUrl?: string;
}

const roleColors: Record<string, string> = {
  admin: 'var(--sapBrandColor)',
  teacher: '#0a7b3c',
  student: '#7b4f0a',
  parent: '#6b3fa0',
};

export function Sidebar({ open, onClose, onSwitchContext, activeContext, availableContexts, onLogout, profileName, profileEmail, profileGender, portalTitle, logoUrl }: SidebarProps) {
  const { t, i18n } = useTranslation();
  const { session } = useSession();
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const toggleLang = () => i18n.changeLanguage(i18n.language === 'en' ? 'fr' : 'en');
  // The rail is off-canvas only below the tablet breakpoint; on larger screens
  // it sits fixed in the layout, so the slide animation is mobile-only.
  const isMobile = useMediaQuery('(max-width: 979px)');
  // Context data comes from an authenticated upstream and may be incomplete
  // during rollout, refresh, or after a revoked assignment. The shell must
  // remain usable while that response is being discarded or refreshed.
  const contextRole = activeContext?.role;
  const contextInstitution = activeContext?.institution;
  const contextGroup = activeContext?.group;
  const educore = useEduCoreLaunchUrl(contextInstitution?.id);
  const financeRole = ['BURSAR', 'ACCOUNTANT', 'CASHIER', 'FINANCIAL_CONTROLLER'].includes(
    contextRole?.code ?? '',
  );

  /**
   * Scanning is offered to seats that may actually scan.
   *
   * This was a denylist — everyone except STUDENT and PARENT — which handed the
   * tool to every role nobody had thought about. An institution director holds
   * no `verification.*` permission at all, so the page loaded, the camera and
   * the code box worked, and the server refused every scan: the worst shape of
   * a permissions bug, because it looks like a broken feature rather than a
   * withheld one. Identity already resolves the seat's permissions; the rail
   * reads them. A service that does not send them leaves the field absent, and
   * the old role rule stands in so a rollout never hides the tool from the gate
   * staff who need it.
   */
  const canScan = activeContext?.permissions
    ? activeContext.permissions.some((permission) => permission.startsWith('verification.'))
    : Boolean(contextRole?.code) && !['STUDENT', 'PARENT'].includes(contextRole?.code ?? '');

  return (
    <>
      {open && <motion.div className="sidebar-overlay" onClick={onClose} role="presentation" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: DURATION.fast }} />}
      <motion.aside
        className={`sidebar ${open ? 'sidebar--open' : ''}`}
        aria-label={t('common.appName')}
        initial={false}
        animate={{ x: isMobile ? (open ? 0 : '-100%') : 0 }}
        transition={{ duration: DURATION.base, ease: EASE }}
      >
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <img src={logoUrl || '/assets/images/campusone_logo.png'} alt={portalTitle || 'CampusOne'} className="sidebar-brandLogo" />
            {portalTitle && <span className="sidebar-brandName">{portalTitle}</span>}
          </div>
          <button className="sidebar-close" onClick={onClose} aria-label={t('common.close')}>
            <Icon name="close" size={20} />
          </button>
        </div>

        <div className="sidebar-profile" aria-label={t('common.account')}>
          <Avatar name={profileName} gender={profileGender} size={40} onClick={() => setProfileOpen(true)} />
          <div className="sidebar-profileInfo">
            <span className="sidebar-profileName">{profileName || ''}</span>
            <span className="sidebar-profileEmail">{profileEmail || ''}</span>
          </div>
        </div>

        {/* A seat is at an institution OR at a group — a group context has no
            institution and renders its group name in the same slot. */}
        {activeContext && contextRole && (contextInstitution || contextGroup) && (
          <div className="sidebar-context">
            <div className="sidebar-contextLabel" id="sidebar-context-label">{t('context.operatingAs')}</div>
            <div className="sidebar-contextValue" role="group" aria-labelledby="sidebar-context-label">
              <span className="sidebar-contextDot" aria-hidden="true" style={{ background: roleColors[contextRole.code] || 'var(--udexColorGrey6)' }} />
              <div>
                <div className="sidebar-contextRole">{contextRole.name}</div>
                <div className="sidebar-contextInstitution">{contextInstitution?.name ?? contextGroup?.name ?? ''}</div>
              </div>
              {availableContexts.length > 1 && (
                <button className="sidebar-switchBtn" onClick={onSwitchContext} aria-label={t('context.switchContext')}>
                  <Icon name="switch" size={16} aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
        )}

        <nav className="sidebar-nav" aria-label={t('navigation.home')}>
          <NavLink to="/" icon="home" label={t('navigation.home')} />
          <NavLink to="/products" icon="products" label={t('navigation.products')} />
          <NavLink to="/notifications" icon="notifications" label={t('navigation.notifications')} />
          {/* The journey timeline is for everyone — a member sees their own day,
              a guardian a linked student's. */}
          <NavLink to="/journey" icon="clock" label={t('navigation.journey')} />
          {/* Scan & verify is a staff tool, and only for seats whose permissions
              say they may scan — see canScan. */}
          {canScan && <NavLink to="/verify" icon="checkmark" label={t('navigation.verify')} />}
          {(['DEAN', 'PRINCIPAL', 'ACADEMIC_DIRECTOR'].includes(contextRole?.code ?? '') ||
            isInstitutionAdmin(contextRole?.code)) && (
            <NavLink to="/approvals" icon="check" label={t('navigation.deanApprovals')} />
          )}
        </nav>

        {financeRole && (
          <div className="sidebar-platform">
            <a
              className="sidebar-platformLink"
              href={educore.href(EduCorePaths.overview) ?? '/products'}
              {...(educore.launchUrl
                ? { target: '_blank', rel: 'noopener noreferrer' }
                : { onClick: onClose })}
            >
              <Icon name="report" size={18} aria-hidden="true" />
              <span className="sidebar-platformText">
                <strong>{t('navigation.educoreFinance')}</strong>
                <span>{t('navigation.educoreFinanceSub')}</span>
              </span>
            </a>
          </div>
        )}

        {/* The server-composed academic menu (Admissions, Students, Classes, … Library)
            used to render here, deep-linking into EduCore. Removed: it duplicated what
            launching EduCore from Products already does, and it doubled the length of
            every sidebar with a second group nobody navigated by. The endpoint and its
            client still exist — see src/api/experience.ts — so this is a decision about
            the shell, not a loss of the capability. */}

        {isInstitutionAdmin(contextRole?.code) && (
          <div className="sidebar-platform">
            <Link className="sidebar-platformLink" to="/admin/institution" onClick={onClose}>
              <Icon name="admin" size={18} aria-hidden="true" />
              <span className="sidebar-platformText">
                <strong>{t('admin.shell.wordmark')}</strong>
                <span>{t('admin.shell.wordmarkSub')}</span>
              </span>
            </Link>
          </div>
        )}

        {/* Offered, not enforced. The console re-checks the role and every
            endpoint behind it checks the token. */}
        {isPlatformAdmin(session?.realmRoles) && (
          <div className="sidebar-platform">
            <Link className="sidebar-platformLink" to="/admin" onClick={onClose}>
              <Icon name="platform" size={18} aria-hidden="true" />
              <span className="sidebar-platformText">
                <strong>{t('navigation.platformConsole')}</strong>
                <span>{t('navigation.platformConsoleSub')}</span>
              </span>
            </Link>
          </div>
        )}

        <div className="sidebar-footer">
          <button className="sidebar-footerBtn" onClick={toggleLang} aria-label={i18n.language === 'en' ? 'Switch to French' : 'Passer en anglais'}>
            <Icon name="globe" size={16} aria-hidden="true" />
            {i18n.language === 'en' ? 'Français' : 'English'}
          </button>
          <button className="sidebar-footerBtn sidebar-footerBtn--danger" onClick={() => setLogoutOpen(true)} aria-label={t('auth.signOut')}>
            <Icon name="logout" size={16} aria-hidden="true" />
            {t('auth.signOut')}
          </button>
        </div>
      </motion.aside>
      <Modal open={logoutOpen} onClose={() => setLogoutOpen(false)} title={t('auth.signOutTitle')} description={t('auth.signOutDescription')}>
        <C1Actions align="end" flush>
          <C1Button variant="secondary" onClick={() => setLogoutOpen(false)}>{t('common.cancel')}</C1Button>
          <C1Button variant="danger" onClick={() => { setLogoutOpen(false); onLogout(); }}>{t('auth.signOut')}</C1Button>
        </C1Actions>
      </Modal>
      <ProfileCard
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        name={profileName}
        email={profileEmail}
        gender={profileGender}
        roleName={contextRole?.name}
        institutionName={contextInstitution?.name}
        groupName={contextGroup?.name}
      />
    </>
  );
}
